import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import {
  Button,
  ClickAwayListener,
  LinearProgress,
  MenuItem,
  MenuList,
  Popper,
} from '@material-ui/core';
import { change, formValueSelector, reduxForm } from 'redux-form/immutable';
import moment from 'moment';

import { blackish, lightGray } from 'utils/color-styles';
import ButtonBadge from 'retired/ButtonBadge';
import Box from 'retired/Box';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { selectDisplayedDonations, selectPendingExternalTotal } from 'entities/donation/selectors';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectDonationMatchingRules } from 'entities/donationMatchingRule/selectors';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import DonationsPageBanner from 'assets/pageBanners/donations.avif';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';

import { PayPalCommerceLink } from './PayPalCommerceLink';
import DonationsOverview from './DonationsOverview';
import reducer from './reducers';
import epics from './epics';
import * as Actions from './actions';
import {
  selectInitialized,
  selectIsOverviewOpen,
  selectLoading,
  selectSelectedDonations,
} from './selectors';
import { FORM_IDENTIFIER } from './constants';
import { DonationsList } from './DonationList';
import { SetupDonationProvider } from './SetupDonationProvider';

const key = 'donationsPage';

const DonationsListPage = (): ReactElement => {
  const history = useHistory();
  const match = useRouteMatch();

  const selector = formValueSelector(FORM_IDENTIFIER);
  const state = useSelector((s) => s);
  const matchingMode = selector(state, 'matching');
  const donations = useSelector(selectDisplayedDonations);
  const loading = useSelector(selectLoading);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const selectedDonations = useSelector(selectSelectedDonations);
  const isOverviewOpen = useSelector(selectIsOverviewOpen);
  const donationMatchingRules = useSelector(selectDonationMatchingRules);
  const initialized = useSelector(selectInitialized);
  const pendingExternalTotal = useSelector(selectPendingExternalTotal);

  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    if (!initialized && user) {
      dispatch(Actions.initAction());
      dispatch(Actions.closeOverviewAction());
      dispatch(change(FORM_IDENTIFIER, 'matching', false));
    }
  }, [user, initialized]);
  useEffect(() => {
    dispatch(
      Actions.setFetchParamsAction({
        type: match.params.type,
      })
    );
  }, [match.params.type]);

  if (!user) {
    return <LinearProgress />;
  }

  if (user && !user.hasPrivilege(Privileges.DONATION_VIEW)) {
    history.replace('/');
  }

  if (match.params.type === 'donors' && !user.hasPrivilege(Privileges.DONATION_INDIVIDUALS_VIEW)) {
    history.replace('/donations/corporate');
  }

  if (isOverviewOpen && selectedDonations.size) {
    return (
      <DonationsOverview
        loading={loading}
        matchingMode={matchingMode}
        donations={donations}
        selectedDonations={selectedDonations}
        closeOverviewAction={(): void => {
          dispatch(Actions.closeOverviewAction());
        }}
        matchDonationsAction={(donationIds: string[]): void => {
          dispatch(Actions.matchDonationsAction(donationIds));
        }}
        unmatchDonationsAction={(donationIds: string[]): void => {
          dispatch(Actions.unmatchDonationsAction(donationIds));
        }}
        organization={organization}
        donationMatchingRules={donationMatchingRules}
      />
    );
  } else {
    // @NOTE-AC: We shouldn't dispatch from render
    dispatch(Actions.closeOverviewAction());
  }

  const isCompany = organization?.isCompany();
  const canDonate = user?.canDonate();

  const setupAllowed =
    user.hasPrivilege(Privileges.DONATION_MANAGE_RULES) &&
    (!isCompany ||
      user.hasFeature(Features.DONATION_MATCHING) ||
      user.hasFeature(Features.CORPORATE_DONATIONS) ||
      user.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS) ||
      user.hasFeature(Features.DONATION_CREDIT));

  const viewIsCorporateDonations = match.params.type === 'corporate';
  const viewSupportsMatching = !viewIsCorporateDonations;
  const budgetLedgerEnabled = isCompany && user.hasPrivilege(Privileges.BUDGET_LEDGER_VIEW);
  const externalMatchingEnabled =
    isCompany &&
    viewSupportsMatching &&
    user.hasPrivilege(Privileges.DONATION_MATCH) &&
    user.hasFeature(Features.EXTERNAL_DONATIONS) &&
    user.hasFeature(Features.EXTERNAL_MATCHING);
  const seedCreditEnabled =
    isCompany &&
    user.hasPrivilege(Privileges.DONATION_CREDIT_MANAGE) &&
    user.hasFeature(Features.DONATION_CREDIT);

  const showDonationsList = isCompany || canDonate;

  const showPaypalCommerceLink =
    user.hasFeature(Features.PAYPAL_COMMERCE_LINK) &&
    user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) &&
    user.organization?.isNonprofit() &&
    !user.organization?.acceptsDonations &&
    user.organization?.payPalModel !== 'COMMERCE';

  return (
    <>
      <Helmet title={match.params.type === 'corporate' ? 'Corporate donations' : 'Donations'} />
      <PageBannerContainer color="#ECCEFF" backgroundImage={DonationsPageBanner}>
        <h1>
          Donations
          {isCompany && setupAllowed && (
            <Link to="/donations/rules">
              <Button
                size="large"
                style={{
                  marginLeft: '1em',
                  borderRadius: '0.25em',
                  color: blackish,
                  borderColor: blackish,
                }}
              >
                <SettingsIcon />
                &nbsp;Budgets &amp; Rules
              </Button>
            </Link>
          )}
        </h1>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.33em 1em',
            justifyContent: 'flex-end',
            marginLeft: '1em',
          }}
        >
          {externalMatchingEnabled && (
            <Link to="/donations/external/pending">
              <ButtonBadge badgeContent={pendingExternalTotal || null} color="primary">
                <Button size="large">External Donation Requests</Button>
              </ButtonBadge>
            </Link>
          )}
          {!!viewIsCorporateDonations && (
            <>
              {user?.hasFeature(Features.CORPORATE_DONATIONS) &&
                !user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS) && (
                  <Button size="large" onClick={() => history.push(`/donations/corporate/donate`)}>
                    Make a Corporate Donation
                  </Button>
                )}
              {viewIsCorporateDonations &&
                !user?.hasFeature(Features.CORPORATE_DONATIONS) &&
                user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS) && (
                  <Button size="large" onClick={() => history.push(`/donations/corporate/log`)}>
                    Log a Corporate Donation
                  </Button>
                )}
              {user?.hasFeature(Features.CORPORATE_DONATIONS) &&
                user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS) && (
                  <>
                    <Button size="large" onClick={handleClick}>
                      Corporate Donations <ArrowDropDownIcon />
                    </Button>
                    <Popper open={!!anchorEl} anchorEl={anchorEl} style={{ width: '200px' }}>
                      <Box style={{ padding: 0 }}>
                        <ClickAwayListener onClickAway={() => handleClick(undefined)}>
                          <MenuList disablePadding>
                            <Link to="/donations/corporate/donate">
                              <MenuItem>Make a Donation</MenuItem>
                            </Link>
                            <Link to="/donations/corporate/log">
                              <MenuItem
                                style={{
                                  borderTop: `1px solid ${lightGray}`,
                                }}
                              >
                                Log External Donation
                              </MenuItem>
                            </Link>
                          </MenuList>
                        </ClickAwayListener>
                      </Box>
                    </Popper>
                  </>
                )}
            </>
          )}
          {budgetLedgerEnabled && (
            <Link to="/donations/ledger">
              <Button size="large">Giving Accounts</Button>
            </Link>
          )}
          {seedCreditEnabled && (
            <Link to="/donations/credit-seeding">
              <Button size="large">Seed Credit</Button>
            </Link>
          )}
        </div>
      </PageBannerContainer>

      {showPaypalCommerceLink && <PayPalCommerceLink />}

      {showDonationsList ? <DonationsList user={user} /> : null}

      {!showDonationsList && !showPaypalCommerceLink ? <SetupDonationProvider /> : null}
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  onChange: (__, dispatch, form) => {
    // Avoid triggering on initialization to avoid duplicate fetch
    if (form.dirty === true) {
      dispatch(Actions.setFetchParamsAction({}));
    }
  },
  initialValues: {
    startingAt: moment().endOf('month').subtract(3, 'months').startOf('month'),
    endingAt: moment().endOf('month'),
    matched: ['Matched', 'Unmatched'],
    paymentMethod: [],
    recurringFrequency: ['0', 'P'],
    matching: false,
    currencies: [],
  },
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(DonationsListPage);
